// components/dashboard/PropertyIntakeForm.jsx
//
// Built directly from:
//   "Tenant Access Vacancy Marketing Intake Form"
// Every section and field matches the actual document Madelyn provided.

import React, { useState } from "react";
import {
  Box, Typography, TextField, Grid, Radio, RadioGroup,
  FormControlLabel, FormControl, FormLabel, Divider, Alert, Button, Stack, Chip,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// ─── Default form state — mirrors the intake doc exactly ──────────────────
const EMPTY = {
  // ── Property Information ──
  address: "",
  city: "",
  state: "",
  zip: "",
  submitterType: "",          
  company: "",

  // ── Property Availability ──
  dateAvailable: "",
  isOccupied: "",            
  occupantName: "",
  occupantPhone: "",
  occupantEmail: "",
  occupantHasPets: "",      
  occupantPetDescription: "",

  // ── Property Management Software ──
  hasPMSLink: "",            
  pmsLink: "",

  // ── Property Details ──
  bedrooms: "",
  bathrooms: "",
  squareFeet: "",
  garageSpaces: "",
  parking: "",
  petPolicy: "",
  hoaInfo: "",
  rent: "",
  securityDeposit: "",
  leaseLength: "",

  // ── Utilities (owner | tenant) ──
  utilElectric: "",
  utilGas: "",
  utilWater: "",
  utilSewer: "",
  utilTrash: "",
  utilInternet: "",
  utilLawnCare: "",
  utilSnowRemoval: "",
  utilOther: "",

  // ── Qualifying Criteria ──
  qualifyingCriteria: "",

  // ── Marketing Photos ──
  photoUploadLink: "",

  // ── Marketing Copy ──
  sellingPoint1: "",
  sellingPoint2: "",
  sellingPoint3: "",
  sellingPoint4: "",
  neighborhoodAmenities: "",
  interiorWord1: "",
  interiorWord2: "",
  interiorWord3: "",
  whatYouLikeMost: "",
  incentives: "",

  // ── Access Information ──
  accessMethod: "",           
  lockboxCode: "",
  gateCode: "",
  alarmInstructions: "",
  specialAccessInstructions: "",
  showingInstructions: "",

  // ── Emergency Contact ──
  emergencyContactName: "",
  emergencyContactPhone: "",

  // ── Ownership Verification ──
  ownershipRole: "",          
};

const REQUIRED_MANUAL = [
  "address", "city", "state", "zip",
  "dateAvailable", "bedrooms", "bathrooms", "rent",
  "accessMethod", "emergencyContactName", "emergencyContactPhone",
];

const UTILITY_FIELDS = [
  { key: "utilElectric",    label: "Electric" },
  { key: "utilGas",         label: "Gas" },
  { key: "utilWater",       label: "Water" },
  { key: "utilSewer",       label: "Sewer" },
  { key: "utilTrash",       label: "Trash" },
  { key: "utilInternet",    label: "Internet" },
  { key: "utilLawnCare",    label: "Lawn Care" },
  { key: "utilSnowRemoval", label: "Snow Removal" },
];

// ─── Sub-components ───────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ mb: 2, mt: 3 }}>
    <Stack direction="row" alignItems="center" spacing={1}>
      {Icon && <Icon color="primary" fontSize="small" />}
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
    </Stack>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
    <Divider sx={{ mt: 1 }} />
  </Box>
);

const UtilityRow = ({ label, fieldKey, value, onChange }) => (
  <Grid item xs={12} sm={6}>
    <FormControl component="fieldset" size="small">
      <FormLabel sx={{ fontSize: "0.82rem", mb: 0.5 }}>{label}</FormLabel>
      <RadioGroup row value={value} onChange={(e) => onChange(fieldKey, e.target.value)}>
        <FormControlLabel value="owner"  control={<Radio size="small" />} label="Owner" />
        <FormControlLabel value="tenant" control={<Radio size="small" />} label="Tenant" />
      </RadioGroup>
    </FormControl>
  </Grid>
);

// ─── Main component ───────────────────────────────────────────────────────

const PropertyIntakeForm = ({ onSubmit, onBack }) => {
  const [infoSource, setInfoSource] = useState("manual");
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const field = (key, extra = {}) => ({
    value: form[key],
    onChange: (e) => set(key, e.target.value),
    error: !!errors[key],
    helperText: errors[key],
    size: "small",
    fullWidth: true,
    ...extra,
  });

  const validate = () => {
    if (infoSource === "pms") return true;
    const errs = {};
    REQUIRED_MANUAL.forEach((k) => {
      if (!String(form[k]).trim()) errs[k] = "Required";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit?.({ infoSource, ...form });
  };

  const isPMS = infoSource === "pms";

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Property Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This information helps us create stronger marketing, position your property
        competitively, and begin showings as quickly as possible.
      </Typography>

      {/* ── Info source choice ── */}
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>
          How would you like to provide property information?
        </FormLabel>
        <RadioGroup
          row
          value={infoSource}
          onChange={(e) => setInfoSource(e.target.value)}
        >
          <FormControlLabel
            value="manual"
            control={<Radio />}
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HomeWorkIcon fontSize="small" />
                <span>Enter manually</span>
              </Stack>
            }
          />
          <FormControlLabel
            value="pms"
            control={<Radio />}
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CloudSyncIcon fontSize="small" />
                <span>Import from Property Management Software</span>
                <Chip label="Coming soon" size="small" variant="outlined" sx={{ fontSize: "0.65rem" }} />
              </Stack>
            }
          />
        </RadioGroup>
      </FormControl>

      {isPMS && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Property Management Software import is not yet available. Please use
          manual entry for now — we'll add this integration soon.
        </Alert>
      )}

      {/* ══════════════════════════════════════════════════════
          SECTION 1 — Property Information
      ══════════════════════════════════════════════════════ */}
      <SectionHeader icon={HomeWorkIcon} title="Property Address" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label="Street Address" {...field("address")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField label="City" {...field("city")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField label="State" {...field("state")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Zip" {...field("zip")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "0.82rem", mb: 0.5 }}>Who is submitting this order?</FormLabel>
            <RadioGroup row value={form.submitterType} onChange={(e) => set("submitterType", e.target.value)}>
              <FormControlLabel value="owner"   control={<Radio size="small" />} label="Property Owner" />
              <FormControlLabel value="manager" control={<Radio size="small" />} label="Property Manager" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Company (if applicable)" {...field("company")} disabled={isPMS} />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════
          SECTION 2 — Property Availability
      ══════════════════════════════════════════════════════ */}
      <SectionHeader title="Property Availability" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date Available"
            type="date"
            InputLabelProps={{ shrink: true }}
            {...field("dateAvailable")}
            disabled={isPMS}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "0.82rem", mb: 0.5 }}>Is the property currently occupied?</FormLabel>
            <RadioGroup row value={form.isOccupied} onChange={(e) => set("isOccupied", e.target.value)}>
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no"  control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {form.isOccupied === "yes" && (
          <>
            <Grid item xs={12} sm={4}>
              <TextField label="Current Occupant Name" {...field("occupantName")} disabled={isPMS} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Phone Number" {...field("occupantPhone")} disabled={isPMS} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Email Address" {...field("occupantEmail")} disabled={isPMS} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <FormLabel sx={{ fontSize: "0.82rem", mb: 0.5 }}>Does the occupant have pets?</FormLabel>
                <RadioGroup row value={form.occupantHasPets} onChange={(e) => set("occupantHasPets", e.target.value)}>
                  <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="no"  control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {form.occupantHasPets === "yes" && (
              <Grid item xs={12}>
                <TextField
                  label="Please describe the pets"
                  {...field("occupantPetDescription")}
                  multiline rows={2}
                  disabled={isPMS}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* ══════════════════════════════════════════════════════
          SECTION 3 — Property Details
      ══════════════════════════════════════════════════════ */}
      <SectionHeader title="Property Details" />
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <TextField label="Bedrooms"     type="number" {...field("bedrooms")}     disabled={isPMS} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Bathrooms"    type="number" {...field("bathrooms")}    disabled={isPMS} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Square Feet"  type="number" {...field("squareFeet")}   disabled={isPMS} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Garage Spaces" type="number" {...field("garageSpaces")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Parking Details" {...field("parking")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Pet Policy" {...field("petPolicy")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="HOA Information" {...field("hoaInfo")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Lease Length" placeholder="e.g. 12 months" {...field("leaseLength")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Monthly Rent"
            type="number"
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
            {...field("rent")}
            disabled={isPMS}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Security Deposit"
            type="number"
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
            {...field("securityDeposit")}
            disabled={isPMS}
          />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════
          SECTION 4 — Utilities
      ══════════════════════════════════════════════════════ */}
      <SectionHeader
        title="Utilities"
        subtitle="Please indicate who pays each utility."
      />
      <Grid container spacing={1}>
        {UTILITY_FIELDS.map(({ key, label }) => (
          <UtilityRow
            key={key}
            label={label}
            fieldKey={key}
            value={form[key]}
            onChange={set}
          />
        ))}
        <Grid item xs={12}>
          <TextField
            label="Other utility notes"
            {...field("utilOther")}
            disabled={isPMS}
            placeholder="Describe any other utility arrangements"
          />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════
          SECTION 5 — Qualifying Criteria
      ══════════════════════════════════════════════════════ */}
      <SectionHeader
        title="Qualifying Criteria"
        subtitle="Provide any qualification standards applicants must meet (credit score, income requirements, occupancy limits, pet restrictions, smoking policy, etc.)"
      />
      <TextField
        label="Qualifying criteria"
        {...field("qualifyingCriteria")}
        multiline rows={3}
        disabled={isPMS}
        placeholder="e.g. 650+ credit score, 3x monthly income, no smoking..."
      />

      {/* ══════════════════════════════════════════════════════
          SECTION 6 — Marketing Photos
      ══════════════════════════════════════════════════════ */}
      <SectionHeader icon={AddPhotoAlternateIcon} title="Marketing Photos" />
      <Alert severity="info" sx={{ mb: 2 }}>
        For best results include: bright lighting, wide-angle room photos, exterior, kitchen,
        bathrooms, bedrooms, garage, yard, and community amenities.
      </Alert>
      <TextField
        label="Photo Upload Link"
        placeholder="Paste a Google Drive, Dropbox, or similar link"
        {...field("photoUploadLink")}
        disabled={isPMS}
      />

      {/* ══════════════════════════════════════════════════════
          SECTION 7 — Help Us Market Your Property
      ══════════════════════════════════════════════════════ */}
      <SectionHeader
        title="Help Us Market Your Property"
        subtitle="What are the FOUR biggest reasons someone should choose this property over a similar one nearby at the same price?"
      />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((n) => (
          <Grid item xs={12} sm={6} key={n}>
            <TextField
              label={`Selling point ${n}`}
              {...field(`sellingPoint${n}`)}
              disabled={isPMS}
              placeholder="e.g. Oversized garage, Mountain views..."
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Tell us about the neighborhood
        </Typography>
        <TextField
          label="Nearby amenities that make this property more desirable"
          {...field("neighborhoodAmenities")}
          multiline rows={2}
          disabled={isPMS}
          placeholder="e.g. Parks, shopping, restaurants, excellent schools, highway access..."
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Describe the interior in three words
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((n) => (
            <Grid item xs={4} key={n}>
              <TextField
                label={`Word ${n}`}
                {...field(`interiorWord${n}`)}
                disabled={isPMS}
                placeholder={["Bright", "Modern", "Neutral"][n - 1]}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField
          label="What do you like most / what do tenants usually love most?"
          {...field("whatYouLikeMost")}
          multiline rows={2}
          disabled={isPMS}
          placeholder="Why did you purchase this property? What do tenants typically love?"
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField
          label="Move-in incentives (if any)"
          {...field("incentives")}
          multiline rows={2}
          disabled={isPMS}
          placeholder="e.g. Free first month, reduced deposit, flexible lease, lawn care included..."
        />
      </Box>

      {/* ══════════════════════════════════════════════════════
          SECTION 8 — Access Information
      ══════════════════════════════════════════════════════ */}
      <SectionHeader icon={LockOutlinedIcon} title="Access Information" subtitle="How will Tenant Access conduct showings?" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset" size="small">
            <RadioGroup
              value={form.accessMethod}
              onChange={(e) => set("accessMethod", e.target.value)}
            >
              <FormControlLabel value="smartLock"         control={<Radio size="small" />} label="Tenant Access Smart Lock" />
              <FormControlLabel value="existingSmartLock" control={<Radio size="small" />} label="Existing Smart Lock" />
              <FormControlLabel value="lockbox"           control={<Radio size="small" />} label="Lockbox" />
              <FormControlLabel value="keyPickup"         control={<Radio size="small" />} label="Key Pickup" />
            </RadioGroup>
            {errors.accessMethod && (
              <Typography variant="caption" color="error">{errors.accessMethod}</Typography>
            )}
          </FormControl>
        </Grid>

        {form.accessMethod === "lockbox" && (
          <Grid item xs={12} sm={6}>
            <TextField label="Lockbox Code" {...field("lockboxCode")} />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField label="Gate Code (if applicable)" {...field("gateCode")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Alarm Instructions (if applicable)" {...field("alarmInstructions")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Special Access Instructions"
            {...field("specialAccessInstructions")}
            multiline rows={2}
            disabled={isPMS}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Showing Instructions"
            {...field("showingInstructions")}
            multiline rows={2}
            disabled={isPMS}
            placeholder="Any special instructions prospective tenants should know?"
          />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════
          SECTION 9 — Emergency Contact
      ══════════════════════════════════════════════════════ */}
      <SectionHeader title="Emergency Contact" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Emergency Contact Name" {...field("emergencyContactName")} disabled={isPMS} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Phone" {...field("emergencyContactPhone")} disabled={isPMS} />
        </Grid>
      </Grid>

      {/* ══════════════════════════════════════════════════════
          SECTION 10 — Certifications (displayed, implied by submit)
      ══════════════════════════════════════════════════════ */}
      <Box sx={{ mt: 3, p: 2, bgcolor: "action.hover", borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          By submitting this form, you confirm that:
        </Typography>
        {[
          "You are the legal owner or authorized property manager with authority to market this property.",
          "You authorize Tenant Access to prepare marketing copy, edit photos, advertise, conduct showings, and communicate with prospective tenants.",
          "No material changes to pricing, lease terms, or listing content will be made without your written approval.",
          "All submitted information is true and accurate to the best of your knowledge.",
        ].map((text) => (
          <Stack key={text} direction="row" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">✔</Typography>
            <Typography variant="body2" color="text.secondary">{text}</Typography>
          </Stack>
        ))}
      </Box>

      {/* ── Actions ── */}
      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
        {onBack && (
          <Button onClick={onBack}>Back</Button>
        )}
        <Button variant="contained" onClick={handleSubmit}>
          {isPMS ? "Continue (PMS Import Pending)" : "Submit Property Info"}
        </Button>
      </Stack>
    </Box>
  );
};

export default PropertyIntakeForm;